import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { rolesEndpoints } from '@/api/endpoints';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

type Props = {
  title: string;
  submitLabel: string;
  defaultName?: string;
  excludeName?: string;
  isSubmitting?: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
};

export const RoleNameForm = ({
  title,
  submitLabel,
  defaultName = '',
  excludeName,
  isSubmitting = false,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();

  const schema = yup.object({
    name: createNameFieldSchema({ getEndpoint: rolesEndpoints.checkName, excludeName }),
  });

  type FormValues = yup.InferType<typeof schema>;
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(schema),
    mode: 'onChange',
    defaultValues: { name: defaultName },
  });

  const { errors, isSubmitting: isValidatingName } = useFormState({
    control: form.control,
    name: 'name',
  });

  const name = useWatch({ control: form.control, name: 'name' });

  // The required error outlives the keystroke that fills the field, so drop it once there is a value.
  const isBlank = !(name ?? '').trim();
  const nameError = errors.name?.type === 'required' && !isBlank ? undefined : errors.name?.message;
  const clearDanglingDescription = nameError ? {} : { 'aria-describedby': undefined };

  const handleInvalid = () => {
    if (!(form.getValues('name') ?? '').trim()) {
      form.setFocus('name');
    }
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col"
        onSubmit={form.handleSubmit((data) => onSubmit((data.name ?? '').trim()), handleInvalid)}
      >
        <div className="flex flex-col items-start gap-1.5 self-stretch p-4">
          <p className="self-stretch text-base leading-none font-medium">{title}</p>
        </div>

        <div className="flex flex-col items-start gap-6 self-stretch px-4 pb-6">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel className={cn(!nameError && 'text-inherit')}>
                  {t('common.name')}
                </FormLabel>
                <FormControl>
                  <Input
                    autoFocus
                    aria-invalid={!!nameError}
                    {...clearDanglingDescription}
                    {...field}
                  />
                </FormControl>
                {nameError && <FormMessage>{nameError}</FormMessage>}
              </FormItem>
            )}
          />
        </div>

        {/* Stays enabled when empty: submitting puts the cursor back in the input. */}
        <div className="flex items-center justify-end gap-2 self-stretch px-4 pb-4">
          <Button
            type="button"
            variant="ghost-neutral"
            size="md"
            className="min-w-20"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" size="md" disabled={isValidatingName} isLoading={isSubmitting}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
};
